import { createContext, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { NotificationsApi } from '../api/notifications';

const TOKEN_KEY = 'notifications_admin_token';

type AuthContextValue = {
  token: string;
  clearToken: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthGate');
  return ctx;
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string>(() => localStorage.getItem(TOKEN_KEY) ?? '');
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Verify stored token on mount.
  useEffect(() => {
    if (!token) { setChecking(false); return; }
    NotificationsApi.health()
      .then(() => setVerified(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      })
      .finally(() => setChecking(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const jwt = await NotificationsApi.login(username.trim(), password);
      localStorage.setItem(TOKEN_KEY, jwt);
      setToken(jwt);
      setVerified(true);
    } catch {
      setError('Invalid username or password.');
    } finally {
      setSubmitting(false);
    }
  };

  const clearToken = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setVerified(false);
    setUsername('');
    setPassword('');
  };

  if (checking) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!token || !verified) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
        }}
      >
        <Paper sx={{ p: 4, width: 380 }} elevation={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <NotificationsActiveIcon color="success" fontSize="large" />
            <Typography variant="h6" fontWeight={700}>
              Notifications Admin
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Sign in with your admin account.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Username"
              size="small"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ mb: 2 }}
              disabled={submitting}
            />
            <TextField
              fullWidth
              label="Password"
              size="small"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              disabled={submitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting || !username.trim() || !password}
            >
              {submitting ? <CircularProgress size={20} /> : 'Sign in'}
            </Button>
          </form>
        </Paper>
      </Box>
    );
  }

  return <AuthContext.Provider value={{ token, clearToken }}>{children}</AuthContext.Provider>;
}

